import * as React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Page, Layouts } from '@strapi/strapi/admin';
import {
  Button,
  Box,
  Flex,
  Typography,
  Loader,
  Modal,
} from '@strapi/design-system';
import { ArrowLeft, Plus } from '@strapi/icons';

import { useTemplatesApi, type StarterTemplate } from '../api/templates';
import { renderEmailHtml } from '../lib/render-preview';
import { PLUGIN_ID } from '../pluginId';

const IFRAME_W = 600;
const IFRAME_H = 820;
const SCALE = 0.29;

const EmailLibrary: React.FC = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const api = useTemplatesApi();

  const [starters, setStarters] = React.useState<StarterTemplate[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [previews, setPreviews] = React.useState<Record<string, string>>({});
  const [inspecting, setInspecting] = React.useState<StarterTemplate | null>(null);
  const [device, setDevice] = React.useState<'desktop' | 'mobile'>('desktop');

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .listStarters()
      .then(({ data }) => {
        if (!cancelled) setStarters(data ?? []);
      })
      .catch(() => {
        if (!cancelled) setStarters([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!starters.length) return;
    let cancelled = false;
    starters.forEach(async (s) => {
      try {
        const html = await renderEmailHtml(s.content);
        if (!cancelled) setPreviews((prev) => ({ ...prev, [s.id]: html }));
      } catch {
        /* skip */
      }
    });
    return () => {
      cancelled = true;
    };
  }, [starters]);

  const handleUseBlank = () => navigate('../new');

  const handleUseStarter = (starter: StarterTemplate) => {
    setInspecting(null);
    navigate('../new', {
      state: {
        starter: {
          id: starter.id,
          subject: starter.subject,
          content: starter.content,
        },
      },
    });
  };

  const title = formatMessage({
    id: `${PLUGIN_ID}.library.title`,
    defaultMessage: 'Email library',
  });

  if (loading) {
    return <Page.Loading />;
  }

  return (
    <Page.Main>
      <Page.Title>{title}</Page.Title>
      <Layouts.Header
        navigationAction={
          <Link to=".." style={{ textDecoration: 'none' }}>
            <Flex gap={2} alignItems="center">
              <ArrowLeft />
              <Typography variant="omega">
                {formatMessage({
                  id: `${PLUGIN_ID}.library.back`,
                  defaultMessage: 'Back',
                })}
              </Typography>
            </Flex>
          </Link>
        }
        title={title}
        subtitle={formatMessage({
          id: `${PLUGIN_ID}.library.subtitle`,
          defaultMessage: 'Choose a template to get started, or start from blank.',
        })}
      />

      <Layouts.Content>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: '16px',
          }}
        >
          <BlankCard
            label={formatMessage({
              id: `${PLUGIN_ID}.library.blank`,
              defaultMessage: 'Blank',
            })}
            onClick={handleUseBlank}
          />
          {starters.map((s) => (
            <ThumbnailCard
              key={s.id}
              title={s.subject || s.id}
              previewHtml={previews[s.id]}
              onClick={() => {
                setInspecting(s);
                setDevice('desktop');
              }}
            />
          ))}
        </div>
      </Layouts.Content>

      {/* Preview dialog */}
      <Modal.Root
        open={!!inspecting}
        onOpenChange={(isOpen) => !isOpen && setInspecting(null)}
      >
        <Modal.Content style={{ maxWidth: '860px', width: '90vw' }}>
          <Modal.Header>
            <Modal.Title>
              {inspecting?.subject || inspecting?.id || ''}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Typography variant="omega" textColor="neutral600">
              {formatMessage({
                id: `${PLUGIN_ID}.library.preview.hint`,
                defaultMessage:
                  'After you select a template, you can change the images, colors, and text.',
              })}
            </Typography>

            <Flex justifyContent="center" gap={1} paddingTop={3} paddingBottom={3}>
              <Button
                variant={device === 'desktop' ? 'default' : 'tertiary'}
                onClick={() => setDevice('desktop')}
                size="S"
              >
                Desktop
              </Button>
              <Button
                variant={device === 'mobile' ? 'default' : 'tertiary'}
                onClick={() => setDevice('mobile')}
                size="S"
              >
                Mobile
              </Button>
            </Flex>

            <Box
              background="neutral150"
              hasRadius
              style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '16px',
                overflow: 'auto',
              }}
            >
              {inspecting && previews[inspecting.id] ? (
                <iframe
                  title="starter-preview"
                  srcDoc={previews[inspecting.id]}
                  sandbox=""
                  style={{
                    width: device === 'mobile' ? '375px' : '100%',
                    maxWidth: device === 'mobile' ? '375px' : '720px',
                    height: '560px',
                    border: '1px solid #eaeaef',
                    background: '#fff',
                    borderRadius: 4,
                  }}
                />
              ) : (
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  style={{ width: '100%', minHeight: '420px' }}
                >
                  <Loader>
                    {formatMessage({
                      id: `${PLUGIN_ID}.library.preview.loading`,
                      defaultMessage: 'Compiling preview…',
                    })}
                  </Loader>
                </Flex>
              )}
            </Box>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Close>
              <Button variant="tertiary">
                {formatMessage({
                  id: `${PLUGIN_ID}.library.cancel`,
                  defaultMessage: 'Cancel',
                })}
              </Button>
            </Modal.Close>
            {inspecting && (
              <Button onClick={() => handleUseStarter(inspecting)}>
                {formatMessage({
                  id: `${PLUGIN_ID}.library.use`,
                  defaultMessage: 'Use template',
                })}
              </Button>
            )}
          </Modal.Footer>
        </Modal.Content>
      </Modal.Root>
    </Page.Main>
  );
};

/* ------------------------------------------------------------------ */

const BlankCard: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => e.key === 'Enter' && onClick()}
    style={{
      cursor: 'pointer',
      borderRadius: 8,
      border: '2px dashed #c0c0cf',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      aspectRatio: '3 / 4',
      background: '#f6f6f9',
      transition: 'border-color 0.15s',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = '#4945ff';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = '#c0c0cf';
    }}
  >
    <Plus style={{ width: 32, height: 32, color: '#8e8ea9' }} />
    <Typography variant="omega" textColor="neutral600" style={{ marginTop: 8 }}>
      {label}
    </Typography>
  </div>
);

const ThumbnailCard: React.FC<{
  title: string;
  previewHtml?: string;
  onClick: () => void;
}> = ({ title, previewHtml, onClick }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => e.key === 'Enter' && onClick()}
    style={{
      cursor: 'pointer',
      borderRadius: 8,
      overflow: 'hidden',
      border: '1px solid #eaeaef',
      background: '#fff',
      transition: 'box-shadow 0.15s',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.1)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = 'none';
    }}
  >
    <div
      style={{
        width: '100%',
        aspectRatio: '3 / 4',
        overflow: 'hidden',
        position: 'relative',
        background: '#f6f6f9',
      }}
    >
      {previewHtml ? (
        <iframe
          srcDoc={previewHtml}
          sandbox=""
          title="thumb"
          style={{
            width: `${IFRAME_W}px`,
            height: `${IFRAME_H}px`,
            transform: `scale(${SCALE})`,
            transformOrigin: 'top left',
            border: 'none',
            pointerEvents: 'none',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
      ) : (
        <Flex
          alignItems="center"
          justifyContent="center"
          style={{ width: '100%', height: '100%' }}
        >
          <Loader small>{'...'}</Loader>
        </Flex>
      )}
    </div>
    <div style={{ padding: '8px 10px' }}>
      <Typography variant="pi" fontWeight="bold" textColor="neutral800" ellipsis>
        {title}
      </Typography>
    </div>
  </div>
);

export default EmailLibrary;
